import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { useSnackbar } from '@lyearn/ui';
import { useAppConfig } from '@/hooks/useAppConfig';
import { usePublishToWebMutation } from '@/modules/corporate/modules/article/graphql/mutation/generated/publishToWeb';
import { Maybe, PublicAccessConfigType } from '@/types/schema';

import getPublicMicroLearningUrl from '../utils/getPublicMicroLearningUrl';

interface UseMicroLearningPublicUrlParams {
  contentId: string;
  contentName?: string;
  publicAccessConfig?: Maybe<PublicAccessConfigType>;
}

const useMicroLearningPublicUrl = ({
  contentId,
  contentName,
  publicAccessConfig,
}: UseMicroLearningPublicUrlParams) => {
  const {
    site: { appUrl },
  } = useAppConfig();
  const { t } = useTranslation(['Common', 'Article']);
  const { showErrorSnackbar, showSnackbar } = useSnackbar();
  const [_, publishToWeb] = usePublishToWebMutation();
  const [isPublishingPublicUrl, setIsPublishingPublicUrl] = useState(false);
  const [publishedPublicAccessConfig, setPublishedPublicAccessConfig] =
    useState<Maybe<PublicAccessConfigType>>(null);

  const resolvedPublicAccessConfig = publishedPublicAccessConfig ?? publicAccessConfig;

  const publicUrl = useMemo(
    () =>
      getPublicMicroLearningUrl({
        appUrl,
        name: contentName,
        publicId: resolvedPublicAccessConfig?.publicId,
      }),
    [appUrl, contentName, resolvedPublicAccessConfig?.publicId],
  );

  const ensurePublicUrl = useCallback(async () => {
    if (publicUrl || isPublishingPublicUrl) {
      return publicUrl || null;
    }

    setIsPublishingPublicUrl(true);

    try {
      const response = await publishToWeb({
        data: {
          entityId: contentId,
          publishToWeb: true,
        },
      });
      const publishedContent = response.data?.publishToWeb?.content;

      if (
        !response.data?.publishToWeb?.success ||
        !publishedContent ||
        !('name' in publishedContent) ||
        !publishedContent.publicAccessConfig
      ) {
        throw new Error('Failed to publish micro-learning');
      }

      const nextPublicUrl = getPublicMicroLearningUrl({
        appUrl,
        name: publishedContent.name,
        publicId: publishedContent.publicAccessConfig.publicId,
      });

      if (!nextPublicUrl) {
        throw new Error('Missing public access data');
      }

      setPublishedPublicAccessConfig(publishedContent.publicAccessConfig);
      showSnackbar({
        message: t('Article:PublishedContentToWeb'),
      });

      return nextPublicUrl;
    } catch {
      showErrorSnackbar({
        message: t('Common:SomethingWentWrong'),
      });

      return null;
    } finally {
      setIsPublishingPublicUrl(false);
    }
  }, [
    appUrl,
    contentId,
    isPublishingPublicUrl,
    publicUrl,
    publishToWeb,
    showErrorSnackbar,
    showSnackbar,
    t,
  ]);

  return {
    ensurePublicUrl,
    isPublishingPublicUrl,
    publicUrl,
  };
};

export default useMicroLearningPublicUrl;
